'use client';

import React from "react";
import { useRouter } from "next/navigation";
import {
  Settings,
  UsersRound,
  Layers,
  Link2,
  ShieldCheck,
  ArrowRight,
  PlayCircle,
} from "lucide-react";

interface SolutionItem {
  title: string;
  description: string;
}

interface SolutionSection {
  category: string;
  slug: string;                // ← new
  icon: React.ReactNode;
  items: SolutionItem[];
}

// simple slugify: lowercase, strip non-alnum, spaces→-
const slugify = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const pandoSolutions: SolutionSection[] = [
  {
    category: "Getting Started",
    slug: "pf-getting-started",
    icon: <PlayCircle className="w-5 h-5 mr-2 text-primary" />,
    items: [
      { title: "Activate Account", description: "Select preferred modules to begin" },
      { title: "Define Sales Organization", description: "Set up hierarchy levels and role definitions" },
      { title: "Onboard Sales Team", description: "Establish SOPs and operational rules" },
      { title: "Map Markets with Census Data", description: "Assign areas and customers strategically" },
      { title: "Build Product Portfolio", description: "Organize categories and product listings" },
    ]
  },
  {
    category: "Build Your Product",
    slug: "pf-build-your-product",
    icon: <Layers className="w-5 h-5 mr-2 text-primary" />,
    items: [
      { title: "Define Workflows", description: "Create activity templates with validations" },
      { title: "Set Targets", description: "Define KPIs and assign performance weightages" },
      { title: "Manage Stakeholders", description: "Configure stakeholder data forms" },
      { title: "Set Up Review System", description: "Enable real-time quality monitoring" },
    ]
  },
  {
    category: "Integrate",
    slug: "pf-integrate",
    icon: <Link2 className="w-5 h-5 mr-2 text-primary" />,
    items: [
      { title: "Integrate Attendance & Payroll", description: "Sync employee attendance with payroll systems" },
      { title: "Integrate Performance & Appraisal", description: "Link performance tracking with appraisals" },
      { title: "Collect Customer Feedback", description: "Gather and analyze feedback for improvement" },
      { title: "Employee Directory", description: "Connect with LDAP, AD, SSO, or SAML" },
      { title: "Data Sync", description: "Enable ETL/ELT pipelines" },
      // { title: "Messaging", description: "Communicate via SMS and WhatsApp" },
    ]
  },
  {
    category: "Manage Performance",
    slug: "pf-manage-performance",
    icon: <ShieldCheck className="w-5 h-5 mr-2 text-primary" />,
    items: [
      { title: "BI Tools Integration", description: "Connect with Business Intelligence tools" },
      { title: "Audit Trails", description: "Track user actions and system changes" },
      { title: "Compliance Management", description: "Ensure regulatory compliance" },
    ]
  },
];

export default function MegaMenuPando({ onClose }: { onClose: () => void }) {
  const router = useRouter();

  const handleItemClick = (sectionSlug: string, title: string) => {
    const titleSlug = slugify(title);
    const targetUrl = `/${sectionSlug}#${titleSlug}`;
    console.log('Navigating to:', targetUrl);
    router.push(targetUrl);
    onClose();
  };

  return (
    <div className="fixed top-14 left-0 right-0 h-[calc(100vh-3.5rem)] z-50 flex flex-col">
    {/* Content block - white background */}
    <div className="bg-white px-10 py-8 flex gap-8 overflow-x-auto">
      {pandoSolutions.map(section => (
        <div key={section.slug} className="min-w-[335px]">
          <div className="flex items-center text-lg font-semibold mb-2 text-gray-800">
            {section.icon}
            <span className="mt-1">{section.category}</span>
          </div>
          <hr className="mb-3 border-gray-300" />
          <ul className="space-y-3">
            {section.items.map(item => (
              <li
                key={item.title}
                className="group flex justify-between items-start hover:text-primary w-full"
              >
                <div className="w-full">
                  <div className="font-medium text-gray-900 group-hover:text-primary truncate">
                    {item.title}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {item.description}
                  </div>
                </div>
                <div className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1 ml-2" />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>

    {/* Bottom filler with blur effect */}
    <div className="flex-1 bg-white/60 backdrop-blur-sm" />
  </div>
  );
}
